import { Box, Typography, type Theme } from '@mui/material';
import {
  costGuideComparisons_costGuideComparisons_CostGuideComparisonsSuccess_costGuideComparisons_geographicRateComparison_locationComparisons as CostGuideLocationComparisonType,
  costGuideComparisons_costGuideComparisons_CostGuideComparisonsSuccess_costGuideComparisons_mostAffordableRateComparison_locationComparisons_location as CostGuideLocationType,
} from '@/__generated__/CostGuideComparisons';

interface ComparisonChartProps {
  chartData: CostGuideLocationComparisonType[];
}

const ComparisonChart = ({ chartData }: ComparisonChartProps) => {
  const barGraphMaxHeightPx = 100;

  const getMaximumHourlyRate = (data: CostGuideLocationComparisonType[]): string => {
    return data
      .reduce((currentRate, currentData) => {
        const maxHourlyRate = currentData.rates?.maximumRate?.maximumHourlyRate?.rate?.amount
          ? parseFloat(currentData.rates.maximumRate.maximumHourlyRate.rate.amount)
          : null;

        if (maxHourlyRate && maxHourlyRate > currentRate) {
          return maxHourlyRate;
        }
        return currentRate;
      }, 0)
      .toFixed(2);
  };
  const maximumHourlyRate = getMaximumHourlyRate(chartData);

  const barHeightPx = (hourlyRate: string, maxHourlyRate: string): string => {
    return `${Math.round(
      (barGraphMaxHeightPx * parseFloat(hourlyRate)) / parseFloat(maxHourlyRate)
    )}px`;
  };

  const getLocation = (locationObject: CostGuideLocationType | null): string => {
    if (!locationObject) return 'US';

    const { city, state } = locationObject;
    if (city && state?.stateCode) {
      return `${city}, ${state.stateCode}`;
    }

    return state.name;
  };

  const barGraph = () => {
    return chartData.map((locationData: CostGuideLocationComparisonType) => {
      const { rates } = locationData;
      const location = getLocation(locationData.location);
      const avgHourlyRate = rates?.averageRate?.averageHourlyRate?.rate?.amount
        ? parseFloat(rates.averageRate.averageHourlyRate.rate.amount).toFixed(2)
        : null;
      const maxHourlyRate = rates?.maximumRate?.maximumHourlyRate?.rate?.amount
        ? parseFloat(rates.maximumRate.maximumHourlyRate.rate.amount).toFixed(2)
        : null;

      return (
        <Box pr={2} key={location}>
          {avgHourlyRate && maxHourlyRate && (
            <>
              <Box display="flex" alignItems="flex-end" pb={1}>
                <Box display="flex" flexDirection="column">
                  <Typography align="center" variant="tag">
                    ${avgHourlyRate}
                  </Typography>
                  <Box
                    width="44px"
                    height={barHeightPx(avgHourlyRate, maximumHourlyRate)}
                    sx={(theme: Theme) => ({
                      backgroundColor: theme.palette.care.blue[500],
                    })}
                  />
                </Box>
                <Box display="flex" flexDirection="column">
                  <Typography align="center" variant="tag">
                    ${maxHourlyRate}
                  </Typography>
                  <Box
                    width="44px"
                    height={barHeightPx(maxHourlyRate, maximumHourlyRate)}
                    sx={(theme: Theme) => ({
                      backgroundColor: theme.palette.care.navy[600],
                    })}
                  />
                </Box>
              </Box>
              <Typography variant="body3" align="center">
                {location}
              </Typography>
            </>
          )}
        </Box>
      );
    });
  };

  return (
    <Box>
      <Box
        display="flex"
        mb={2}
        alignItems="flex-end"
        sx={{
          '& div:last-child': {
            paddingRight: 0,
          },
        }}>
        {barGraph()}
      </Box>
      <Box display="flex">
        <Box display="flex" alignItems="center" mr={5}>
          <Box
            width="15px"
            height="15px"
            mr={1}
            sx={(theme: Theme) => ({
              backgroundColor: theme.palette.care.blue[500],
            })}
          />
          <Typography variant="info1">Starting rate (avg)</Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <Box
            width="15px"
            height="15px"
            mr={1}
            sx={(theme: Theme) => ({
              backgroundColor: theme.palette.care.navy[600],
            })}
          />
          <Typography variant="info1">Max rate (avg)</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ComparisonChart;
